import React from "react";
import { motion } from "framer-motion";
import {
  fadeInUp,
  fadeInLeft,
  fadeInRight,
  staggerContainer,
  staggerItem,
} from "../utils/animations";

const AboutSection = () => {
  return (
    <motion.section
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full mt-32 relative"
      initial="initial"
      whileInView="animate"
      viewport={{ once: true, amount: 0.3 }}
      variants={staggerContainer}
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-full bg-primary/5 blur-[120px] -z-10 rounded-full parallax-bg"></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20 items-center">
        <motion.div
          className="relative order-2 md:order-1"
          variants={fadeInLeft}
        >
          <div className="relative z-10 space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-6 flex items-center gap-3">
                <span className="w-12 h-1 bg-accent rounded-full"></span>
                About Me
              </h2>
              <div className="space-y-6 text-gray-400 leading-relaxed text-lg text-justify">
                <p>
                  Hello! I'm{" "}
                  <span className="text-white font-medium ">
                    Nazmus Sakib
                  </span>
                  . My programming journey began in 2025 with Programming-Hero,
                  when a simple attempt to modify a web application unexpectedly
                  revealed the joy of making things work with code. That moment
                  sparked a passion, and since then I've been captivated by the
                  endless possibilities of web development. As a Frontend
                  Developer, I specialize in building fast, scalable, and
                  visually stunning user interfaces. I focus on crafting
                  accessible, human-centered digital experiences that combine
                  clean architecture with pixel-perfect design.
                </p>
                <p>
                  I thrive in dynamic environments where I can wear multiple
                  hats — from structuring reusable React components and managing
                  state efficiently, to fine-tuning animations and ensuring
                  responsive layouts across devices. For me, development is more
                  than just writing code; it's about creating meaningful
                  experiences that empower users and bring ideas to life.
                </p>
                <p>When I'm not glued to my screen, you can find me:</p>
              </div>
            </div>
            <motion.div
              className="grid grid-cols-1 sm:grid-cols-2 gap-4"
              variants={staggerContainer}
            >
              <motion.div
                className="bg-white/5 border border-white/10 rounded-2xl p-4 hover:bg-white/10 transition-colors flex items-center gap-4 hover-card"
                variants={staggerItem}
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary">
                  <span className="material-symbols-outlined">
                    sports_basketball
                  </span>
                </div>
                <div>
                  <h4 className="font-bold text-white">Sports</h4>
                  <p className="text-xs text-gray-400">Weekend Cricket</p>
                </div>
              </motion.div>
              <motion.div
                className="bg-white/5 border border-white/10 rounded-2xl p-4 hover:bg-white/10 transition-colors flex items-center gap-4 hover-card"
                variants={staggerItem}
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center text-accent">
                  <span className="material-symbols-outlined">palette</span>
                </div>
                <div>
                  <h4 className="font-bold text-white">Painting</h4>
                  <p className="text-xs text-gray-400">Acrylic on canvas</p>
                </div>
              </motion.div>
              <motion.div
                className="bg-white/5 border border-white/10 rounded-2xl p-4 hover:bg-white/10 transition-colors flex items-center gap-4 hover-card"
                variants={staggerItem}
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center text-green-400">
                  <span className="material-symbols-outlined">flight</span>
                </div>
                <div>
                  <h4 className="font-bold text-white">Travel</h4>
                  <p className="text-xs text-gray-400">
                    Exploring new cultures
                  </p>
                </div>
              </motion.div>
              <motion.div
                className="bg-white/5 border border-white/10 rounded-2xl p-4 hover:bg-white/10 transition-colors flex items-center gap-4 hover-card"
                variants={staggerItem}
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-400">
                  <span className="material-symbols-outlined">menu_book</span>
                </div>
                <div>
                  <h4 className="font-bold text-white">Reading</h4>
                  <p className="text-xs text-gray-400">Sci-fi & Philosophy</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
        <motion.div
          className="order-1 md:order-2 relative"
          variants={fadeInRight}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary via-accent to-purple-600 rounded-[2rem] rotate-3 opacity-20 blur-2xl"></div>
          <div className="bg-card-bg/50 backdrop-blur-sm border border-white/10 rounded-[2rem] p-8 relative z-10 overflow-hidden group">
            <div className="absolute top-0 right-0 p-24 bg-primary/10 rounded-full blur-3xl -mr-12 -mt-12"></div>
            <h3 className="text-2xl font-display font-bold text-white mb-6">
              Education
            </h3>
            <a
              className="flex items-center gap-4 mb-8 group/edu hover:bg-white/5 p-2 -ml-2 rounded-xl transition-all"
              href="#education"
            >
              <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center text-primary group-hover/edu:scale-110 group-hover/edu:bg-primary/20 transition-all border border-primary/10">
                <span className="material-symbols-outlined text-3xl">
                  school
                </span>
              </div>
              <div>
                <p className="text-white font-bold text-lg group-hover/edu:text-primary transition-colors">
                  Bachelor of Social Sciences
                </p>
                <p className="text-accent text-sm">International Relations</p>
                <p className="text-gray-400 text-xs font-medium mt-1 flex items-center gap-1">
                  Gopalganj Science & Technology University
                  {/* <span className="material-symbols-outlined text-[12px] group-hover/edu:translate-x-0.5 transition-transform">
                    arrow_forward
                  </span> */}
                </p>
              </div>
            </a>
            <h3 className="text-2xl font-display font-bold text-white mb-4">
              Quick Stats
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400 flex items-center gap-2">
                  <span className="material-symbols-outlined text-base">
                    coffee
                  </span>
                  Coffee consumed
                </span>
                <span className="text-white font-bold">∞ Cups</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400 flex items-center gap-2">
                  <span className="material-symbols-outlined text-base">
                    language
                  </span>
                  Languages Spoken
                </span>
                <span className="text-white font-bold">
                  English, Bengali, Hindi, Spanish, French
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default AboutSection;
